import { useEffect, useState } from "react";
import type { PresenceChannel } from "pusher-js";
import { usePusher } from "./usePusher";

export interface PresenceMember {
	id: string;
	info?: any;
}

export interface Members {
	[key: string]: PresenceMember;
}

interface UsePresenceChannelResult {
	channel: PresenceChannel | null;
	members: Members;
}

export const usePresenceChannel = (
	channelName: string,
): UsePresenceChannelResult => {
	const { client } = usePusher();
	const [channel, setChannel] = useState<PresenceChannel | null>(null);
	const [members, setMembers] = useState<Members>({});

	useEffect(() => {
		if (!client) return;

		// Subscribe to presence channel
		const presenceChannel = client.subscribe(channelName) as PresenceChannel;

		setChannel(presenceChannel);

		// Handle subscription success - initialize members
		const handleSubscriptionSucceeded = () => {
			const initialMembers: Members = {};
			presenceChannel.members.each((member: PresenceMember) => {
				initialMembers[member.id] = member;
			});
			setMembers(initialMembers);
		};

		// Handle member added
		const handleMemberAdded = (member: PresenceMember) => {
			setMembers((prev) => ({
				...prev,
				[member.id]: member,
			}));
		};

		// Handle member removed
		const handleMemberRemoved = (member: PresenceMember) => {
			setMembers((prev) => {
				const updated = { ...prev };
				delete updated[member.id];
				return updated;
			});
		};

		// Bind presence events
		presenceChannel.bind(
			"pusher:subscription_succeeded",
			handleSubscriptionSucceeded,
		);
		presenceChannel.bind("pusher:member_added", handleMemberAdded);
		presenceChannel.bind("pusher:member_removed", handleMemberRemoved);

		// Cleanup
		return () => {
			presenceChannel.unbind(
				"pusher:subscription_succeeded",
				handleSubscriptionSucceeded,
			);
			presenceChannel.unbind("pusher:member_added", handleMemberAdded);
			presenceChannel.unbind("pusher:member_removed", handleMemberRemoved);
			client.unsubscribe(channelName);
			setChannel(null);
			setMembers({});
		};
	}, [client, channelName]);

	return { channel, members };
};
